import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useMemo } from "react";
import "./App.css";

import LoginPage from "./pages/Login";
import ProfilePage from "./pages/Profile";
import HomePage from "./pages/Home";
import VideoSelectionPage from "./pages/VideoSelection";
import GenerateReportPage from "./pages/GenerateReport";
import EditSectionPage from "./pages/EditSection";
import ConfirmationPage from "./pages/Confirmation";
import NotFound404 from "./pages/NotFound";
import { ExtractedInfoPage } from "./pages/ExtractedInfo";
import { FinalEditsPage } from "./pages/FinalEdits";
import ReviewPlanPage from "./pages/ReviewPlan";
import { ReviewPlanRouteGuard } from "./pages/ReviewPlan/ReviewPlanRouteGuard";
import ManageUsersPage from "./pages/Admin/ManageUsers";
import { ProtectedView } from "./components/layout/ProtectedView";
import { ConnectBodycam } from "./pages/ConnectBodycam";
import { AnalyticsPage } from "./pages/Analytics";
import { AuthTokenBridge } from "./components/auth/AuthTokenBridge";
import { AuthCallback } from "./pages/AuthCallback";
import { InitiateLogin } from "./pages/InitiateLogin";
import { RequirePermission } from "./components/auth/RequirePermission";
import { USERS_ADMIN_PERMISSION } from "./shared/auth/authorization";
import { ColorModeProvider, useColorMode } from "./context/ColorModeContext";
import { AuthContextProvider } from "./context/AuthContext";
import { RequireRole } from "./components/auth/RequireRole";
import {
  KardaAdminPage,
  KardaStationDetailPage,
  StationDetailsSection,
  StationUsersSection,
  StationBodycamSection,
  StationSettingsSection,
  KardaAdminProvider,
} from "./pages/KardaAdmin";
import KardaAdminLayout from "./pages/KardaAdmin/KardaAdminLayout";
import TemplateUploadPage from "./pages/TemplateUpload/TemplateUploadPage";

const sharedBreakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
    xxl: 2560, // 2K/QHD displays
    uhd: 3840, // 4K/UHD displays
  },
};

const sharedComponents = {
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        // Fluid font scaling: 16px base, scales up to 22px on 4K
        fontSize: "clamp(16px, calc(14px + 0.2vw), 22px)",
      },
    },
  },
};

function buildTheme(mode: "light" | "dark") {
  return createTheme({
    palette:
      mode === "light"
        ? {
            mode: "light",
            primary: { main: "#26293B" },
            secondary: { main: "#D9D9D9" },
          }
        : {
            mode: "dark",
            primary: { main: "#61afef" }, // One Dark Pro Blue
            secondary: { main: "#c678dd" }, // One Dark Pro Purple
            error: { main: "#e06c75" }, // One Dark Pro Red
            warning: { main: "#d19a66" }, // One Dark Pro Orange
            info: { main: "#61afef" }, // One Dark Pro Blue
            success: { main: "#98c379" }, // One Dark Pro Green
            background: {
              default: "#16191d", // One Dark Pro Night Flat Background
              paper: "#21252b", // Slightly lighter for cards/surfaces
            },
            text: {
              primary: "#e8eaed", // Bright enough for UI readability on dark backgrounds
              secondary: "#b0b5bd", // Muted but still legible for secondary text
            },
          },
    breakpoints: sharedBreakpoints,
    components: {
      ...sharedComponents,
      MuiButton: {
        styleOverrides: {
          outlined: ({ theme }) => ({
            borderColor: alpha(theme.palette.text.primary, 0.3),
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          outlined: ({ theme }) => ({
            borderColor: alpha(theme.palette.text.primary, 0.3),
            color: theme.palette.text.primary,
          }),
        },
      },
    },
  });
}

function AppContent() {
  const { mode } = useColorMode();
  const theme = useMemo(() => buildTheme(mode), [mode]);

  const protectedPages = (
    <>
      <Route path="/" element={<HomePage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/video-selection" element={<VideoSelectionPage />} />
      <Route
        path="/generate-report/:evidenceID"
        element={<GenerateReportPage />}
      />
      <Route
        path="/preview-report-info/:reportID"
        element={<ExtractedInfoPage />}
      />
      <Route path="/edit-section/:reportID" element={<EditSectionPage />} />
      <Route
        path="/review-plan/:reportID?"
        element={
          <ReviewPlanRouteGuard>
            <ReviewPlanPage />
          </ReviewPlanRouteGuard>
        }
      />
      <Route path="/final-edits/:reportID" element={<FinalEditsPage />} />
      <Route
        path="/confirmation/:eventNumber?"
        element={<ConfirmationPage />}
      />
      <Route path="/analytics" element={<AnalyticsPage />} />
      <Route
        path="/manage-users"
        element={
          <RequirePermission permission={USERS_ADMIN_PERMISSION}>
            <ManageUsersPage />
          </RequirePermission>
        }
      />
      <Route
        path="/karda-admin"
        element={
          <RequireRole roleSlug="org-karda-admin">
            <KardaAdminProvider />
          </RequireRole>
        }
      >
        <Route element={<KardaAdminLayout />}>
          <Route index element={<KardaAdminPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="templates" element={<TemplateUploadPage />} />
          <Route
            path="stations/:stationId"
            element={<KardaStationDetailPage />}
          >
            <Route index element={<StationDetailsSection />} />
            <Route path="users" element={<StationUsersSection />} />
            <Route path="bodycam" element={<StationBodycamSection />} />
            <Route path="settings" element={<StationSettingsSection />} />
          </Route>
        </Route>
      </Route>
      <Route
        path="/connect-bodycam"
        element={
          <RequirePermission permission={USERS_ADMIN_PERMISSION}>
            <ConnectBodycam />
          </RequirePermission>
        }
      />
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthTokenBridge />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/initiate-login" element={<InitiateLogin />} />
          <Route path="/callback" element={<AuthCallback />} />
          <Route
            path="/"
            element={
              <ProtectedView>
                <Outlet />
              </ProtectedView>
            }
          >
            {protectedPages}
          </Route>
          <Route path="*" element={<NotFound404 />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ColorModeProvider>
      <AuthContextProvider>
        <AppContent />
      </AuthContextProvider>
    </ColorModeProvider>
  );
}

export default App;
